/* ==========================================================================
   C13 HEALTH - Core interaction layer
   Zero dependencies. Deferred. IntersectionObserver-driven.
   ========================================================================== */
(function () {
  'use strict';

  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  var $  = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  function on(el, ev, fn, opt) { if (el) el.addEventListener(ev, fn, opt || false); }
  function raf(fn) { return window.requestAnimationFrame ? requestAnimationFrame(fn) : setTimeout(fn, 16); }

  function throttle(fn, wait) {
    var last = 0, timer = null;
    return function () {
      var now = Date.now(), ctx = this, args = arguments;
      if (now - last >= wait) { last = now; fn.apply(ctx, args); }
      else if (!timer) {
        timer = setTimeout(function () { timer = null; last = Date.now(); fn.apply(ctx, args); }, wait - (now - last));
      }
    };
  }
  function debounce(fn, wait) {
    var t; return function () {
      var ctx = this, args = arguments;
      clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* =========================================================
     Namespace
     ========================================================= */
  var C13 = window.C13 = window.C13 || {};
  C13.$ = $; C13.$$ = $$; C13.on = on; C13.throttle = throttle; C13.debounce = debounce;
  C13.reducedMotion = function () { return RM.matches; };

  /* =========================================================
     1. SCROLL REVEAL
     ========================================================= */
  var revealIO = null;
  function initReveal(root) {
    var nodes = $$('[data-c13-reveal], [data-c13-stagger]', root || document);
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window) || RM.matches) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          var delay = parseInt(el.getAttribute('data-c13-delay') || '0', 10);
          if (delay) { setTimeout(function () { el.classList.add('is-in'); }, delay); }
          else { el.classList.add('is-in'); }
          if (el.getAttribute('data-c13-repeat') !== 'true') revealIO.unobserve(el);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    nodes.forEach(function (n) { if (!n.classList.contains('is-in')) revealIO.observe(n); });
  }
  C13.initReveal = initReveal;

  /* =========================================================
     2. LAZY IMAGE FADE
     ========================================================= */
  function initLazyFade(root) {
    $$('.c13-img', root || document).forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) { img.classList.add('is-loaded'); return; }
      on(img, 'load', function () { img.classList.add('is-loaded'); });
      on(img, 'error', function () { img.classList.add('is-loaded'); });
    });
  }
  C13.initLazyFade = initLazyFade;

  /* =========================================================
     3. HEADER - sticky, hide-on-scroll, mega menu
     ========================================================= */
  function initHeader() {
    var header = $('[data-c13-header]');
    if (!header) return;

    var lastY = window.pageYOffset;
    var mode  = header.getAttribute('data-sticky') || 'on-scroll-up';

    var onScroll = throttle(function () {
      var y = window.pageYOffset;
      header.classList.toggle('is-stuck', y > 8);
      if (mode === 'on-scroll-up') {
        var openMenu = header.querySelector('.c13-mega.is-open');
        if (!openMenu && y > 240 && y > lastY) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
      }
      lastY = y;
      document.documentElement.style.setProperty('--c13-header-offset', header.offsetHeight + 'px');
    }, 100);

    on(window, 'scroll', onScroll, { passive: true });
    onScroll();

    /* ---- Mega menu ---- */
    var closeTimer = null;
    var triggers = $$('[data-c13-mega-trigger]', header);

    function closeAll(except) {
      triggers.forEach(function (t) {
        if (t === except) return;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        t.setAttribute('aria-expanded', 'false');
        if (panel) panel.classList.remove('is-open');
      });
    }
    function open(trigger) {
      clearTimeout(closeTimer);
      closeAll(trigger);
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      trigger.setAttribute('aria-expanded', 'true');
      if (panel) panel.classList.add('is-open');
    }
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeAll(null); }, 180);
    }

    triggers.forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));

      on(trigger, 'mouseenter', function () { open(trigger); });
      on(trigger, 'focus', function () { open(trigger); });
      on(trigger, 'click', function (e) {
        e.preventDefault();
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) { closeAll(null); } else { open(trigger); }
      });
      on(trigger, 'mouseleave', scheduleClose);
      if (panel) {
        on(panel, 'mouseenter', function () { clearTimeout(closeTimer); });
        on(panel, 'mouseleave', scheduleClose);
      }
    });

    on(document, 'keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
    on(document, 'click', function (e) {
      if (!header.contains(e.target)) closeAll(null);
    });
  }

  /* =========================================================
     4. DRAWERS (mobile nav, filters, cart)
     ========================================================= */
  var lockCount = 0;
  function lockScroll(lock) {
    if (lock) {
      lockCount++;
      if (lockCount === 1) {
        var sw = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (sw > 0) document.body.style.paddingRight = sw + 'px';
      }
    } else {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) { document.body.style.overflow = ''; document.body.style.paddingRight = ''; }
    }
  }
  C13.lockScroll = lockScroll;

  function trapFocus(container) {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    function handler(e) {
      if (e.key !== 'Tab') return;
      var f = $$(sel, container).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    return function () { container.removeEventListener('keydown', handler); };
  }
  C13.trapFocus = trapFocus;

  function initDrawers() {
    $$('[data-c13-drawer]').forEach(function (drawer) {
      var id = drawer.getAttribute('data-c13-drawer');
      var opener = $$('[data-c13-drawer-open="' + id + '"]');
      var closers = $$('[data-c13-drawer-close]', drawer);
      var scrim = $('.c13-drawer__scrim', drawer);
      var release = null, lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        lockScroll(true);
        release = trapFocus(drawer);
        opener.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        var first = $('[data-c13-autofocus]', drawer) || $('.c13-drawer__panel', drawer);
        if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
      }
      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        lockScroll(false);
        if (release) { release(); release = null; }
        opener.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }

      opener.forEach(function (b) { on(b, 'click', function (e) { e.preventDefault(); open(); }); });
      drawer.c13Open = open;
      drawer.c13Close = close;
      closers.forEach(function (b) { on(b, 'click', close); });
      on(scrim, 'click', close);
      on(drawer, 'keydown', function (e) { if (e.key === 'Escape') close(); });

      drawer.c13Open = open;
      drawer.c13Close = close;
    });
  }

  /* =========================================================
     5. ACCORDIONS (FAQ, facets, mobile nav)
     ========================================================= */
  function initAccordions(root) {
    $$('[data-c13-acc]', root || document).forEach(function (group) {
      var single = group.getAttribute('data-c13-acc') === 'single';
      $$('[data-c13-acc-btn]', group).forEach(function (btn) {
        if (btn.dataset.c13Bound) return;
        btn.dataset.c13Bound = '1';
        on(btn, 'click', function () {
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          if (single && !expanded) {
            $$('[data-c13-acc-btn]', group).forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
          }
          btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        });
      });
    });
  }
  C13.initAccordions = initAccordions;

  /* =========================================================
     6. TABS
     ========================================================= */
  function initTabs(root) {
    $$('[data-c13-tabs]', root || document).forEach(function (wrap) {
      var btns = $$('[role="tab"]', wrap);
      function select(btn) {
        btns.forEach(function (b) {
          var on_ = b === btn;
          b.setAttribute('aria-selected', on_ ? 'true' : 'false');
          b.setAttribute('tabindex', on_ ? '0' : '-1');
          var p = document.getElementById(b.getAttribute('aria-controls'));
          if (p) p.hidden = !on_;
        });
      }
      btns.forEach(function (btn, i) {
        on(btn, 'click', function () { select(btn); });
        on(btn, 'keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = btns[(i + 1) % btns.length];
          if (e.key === 'ArrowLeft')  next = btns[(i - 1 + btns.length) % btns.length];
          if (next) { e.preventDefault(); next.focus(); select(next); }
        });
      });
    });
  }
  C13.initTabs = initTabs;

  /* =========================================================
     7. MODALS
     ========================================================= */
  function initModals() {
    $$('[data-c13-modal]').forEach(function (modal) {
      var id = modal.getAttribute('data-c13-modal');
      var openers = $$('[data-c13-modal-open="' + id + '"]');
      var release = null, lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        lockScroll(true);
        release = trapFocus(modal);
        var f = $('[data-c13-modal-close]', modal);
        if (f) setTimeout(function () { f.focus({ preventScroll: true }); }, 60);
      }
      function close() {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        lockScroll(false);
        if (release) { release(); release = null; }
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }
      openers.forEach(function (b) { on(b, 'click', function (e) { e.preventDefault(); open(); }); });
      $$('[data-c13-modal-close]', modal).forEach(function (b) { on(b, 'click', close); });
      on($('.c13-modal__scrim', modal), 'click', close);
      on(modal, 'keydown', function (e) { if (e.key === 'Escape') close(); });
      modal.c13Open = open; modal.c13Close = close;
    });
  }

  /* =========================================================
     8. TOASTS
     ========================================================= */
  function toast(message, linkText, linkHref) {
    var host = $('.c13-toasts');
    if (!host) {
      host = document.createElement('div');
      host.className = 'c13-toasts';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }
    var el = document.createElement('div');
    el.className = 'c13-toast';
    var icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
    el.innerHTML = icon + '<span></span>';
    el.querySelector('span').textContent = message;
    if (linkText && linkHref) {
      var a = document.createElement('a');
      a.href = linkHref; a.textContent = linkText;
      el.appendChild(a);
    }
    host.appendChild(el);
    raf(function () { raf(function () { el.classList.add('is-in'); }); });
    setTimeout(function () {
      el.classList.remove('is-in');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 400);
    }, 4200);
  }
  C13.toast = toast;

  /* =========================================================
     9. WISHLIST  (localStorage, cross-page)
     ========================================================= */
  var WKEY = 'c13:wishlist:v1';
  var Wish = {
    read: function () {
      try { return JSON.parse(localStorage.getItem(WKEY) || '[]'); }
      catch (e) { return []; }
    },
    write: function (list) {
      try { localStorage.setItem(WKEY, JSON.stringify(list)); } catch (e) {}
      document.dispatchEvent(new CustomEvent('c13:wishlist:change', { detail: { list: list } }));
    },
    has: function (handle) { return this.read().indexOf(handle) > -1; },
    toggle: function (handle) {
      var list = this.read();
      var i = list.indexOf(handle);
      if (i > -1) { list.splice(i, 1); } else { list.unshift(handle); }
      this.write(list);
      return i === -1;
    },
    remove: function (handle) {
      var list = this.read(), i = list.indexOf(handle);
      if (i > -1) { list.splice(i, 1); this.write(list); }
    },
    count: function () { return this.read().length; }
  };
  C13.wishlist = Wish;

  function syncWishButtons() {
    var list = Wish.read();
    $$('[data-c13-wish]').forEach(function (btn) {
      var h = btn.getAttribute('data-c13-wish');
      btn.setAttribute('aria-pressed', list.indexOf(h) > -1 ? 'true' : 'false');
    });
    /* The visible "0" has to appear in the link's accessible name, or the
       label/content mismatch check fails. */
    $$('[data-c13-wish-label]').forEach(function (el) {
      el.setAttribute('aria-label', 'Saved items (' + list.length + ')');
    });
    $$('[data-c13-wish-count]').forEach(function (el) {
      var n = list.length;
      el.textContent = n;
      el.classList.toggle('is-visible', n > 0);
    });
  }

  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-c13-wish]') : null;
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-c13-wish');
      var title  = btn.getAttribute('data-c13-wish-title') || 'Item';
      var added  = Wish.toggle(handle);
      btn.classList.add('is-pop');
      setTimeout(function () { btn.classList.remove('is-pop'); }, 420);
      toast(added ? title + ' saved to your list' : title + ' removed', added ? 'View list' : null, added ? '/pages/wishlist' : null);
    });
    document.addEventListener('c13:wishlist:change', syncWishButtons);
    on(window, 'storage', function (e) { if (e.key === WKEY) syncWishButtons(); });
    syncWishButtons();
  }

  /* =========================================================
     10. RECENTLY VIEWED
     ========================================================= */
  var RKEY = 'c13:recent:v1';
  C13.recent = {
    read: function () { try { return JSON.parse(localStorage.getItem(RKEY) || '[]'); } catch (e) { return []; } },
    push: function (handle) {
      if (!handle) return;
      var list = this.read().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      try { localStorage.setItem(RKEY, JSON.stringify(list.slice(0, 12))); } catch (e) {}
    }
  };

  /* =========================================================
     11. ANNOUNCEMENT ROTATOR
     ========================================================= */
  function initAnnouncement() {
    $$('[data-c13-rotator]').forEach(function (track) {
      var slides = $$('.c13-announcement__slide', track);
      if (slides.length < 2 || RM.matches) return;
      var i = 0;
      var speed = parseInt(track.getAttribute('data-speed') || '5', 10) * 1000;
      setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, speed);
    });
  }

  /* =========================================================
     12. JOURNEY progress rail
     ========================================================= */
  function initJourney() {
    var rail = $('[data-c13-journey]');
    if (!rail) return;
    var bars = $$('[data-c13-journey-progress]');
    var chapters = $$('.c13-chapter', rail);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-in', e.isIntersecting); });
      }, { rootMargin: '-30% 0px -30% 0px' });
      chapters.forEach(function (c) { io.observe(c); });
    }

    var update = throttle(function () {
      var rect = rail.getBoundingClientRect();
      var vh = window.innerHeight;
      var total = rect.height - vh;
      var scrolled = -rect.top;
      var pct = total > 0 ? Math.min(100, Math.max(0, (scrolled / total) * 100)) : 0;
      rail.style.setProperty('--c13-progress', pct.toFixed(2) + '%');
      bars.forEach(function (b) { b.style.setProperty('--c13-progress', pct.toFixed(2) + '%'); });
      var idx = Math.min(chapters.length, Math.max(1, Math.ceil((pct / 100) * chapters.length) || 1));
      $$('[data-c13-journey-index]').forEach(function (el) { el.textContent = idx; });
    }, 60);

    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update);
    update();
  }

  /* =========================================================
     13. READING PROGRESS (article)
     ========================================================= */
  function initReadingProgress() {
    var bar = $('[data-c13-readprogress]');
    var target = $('[data-c13-readtarget]');
    if (!bar || !target) return;
    var update = throttle(function () {
      var rect = target.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var pct = total > 0 ? Math.min(100, Math.max(0, (-rect.top / total) * 100)) : 0;
      bar.style.setProperty('--c13-progress', pct.toFixed(2) + '%');
    }, 50);
    on(window, 'scroll', update, { passive: true });
    update();

    /* TOC current-section highlight */
    var links = $$('[data-c13-toc] a');
    if (links.length && 'IntersectionObserver' in window) {
      var map = {};
      links.forEach(function (a) {
        var el = document.getElementById(a.getAttribute('href').slice(1));
        if (el) map[el.id] = a;
      });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            links.forEach(function (a) { a.classList.remove('is-current'); });
            if (map[e.target.id]) map[e.target.id].classList.add('is-current');
          }
        });
      }, { rootMargin: '-10% 0px -75% 0px' });
      Object.keys(map).forEach(function (id) { io.observe(document.getElementById(id)); });
    }
  }

  /* =========================================================
     14. HERO SPARKS
     ========================================================= */
  function initSparks() {
    var host = $('[data-c13-sparks]');
    if (!host || RM.matches) return;
    if (window.innerWidth < 750) return;
    var n = parseInt(host.getAttribute('data-count') || '18', 10);
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var s = document.createElement('span');
      s.className = 'c13-spark';
      s.style.left = (Math.random() * 100) + '%';
      s.style.setProperty('--dx', (Math.random() * 80 - 40) + 'px');
      s.style.animationDuration = (7 + Math.random() * 9) + 's';
      s.style.animationDelay = (Math.random() * 9) + 's';
      s.style.width = s.style.height = (1.5 + Math.random() * 2.5) + 'px';
      s.style.opacity = String(0.3 + Math.random() * 0.5);
      frag.appendChild(s);
    }
    host.appendChild(frag);
  }

  /* =========================================================
     15. REEL LIGHTBOX
     ========================================================= */
  function initReels() {
    var lb = $('[data-c13-lightbox]');
    if (!lb) return;
    var frame = $('.c13-lightbox__frame', lb);
    var reels = $$('[data-c13-reel]');
    var idx = 0, release = null, lastFocus = null;

    function render(i) {
      idx = (i + reels.length) % reels.length;
      var r = reels[idx];
      var src = r.getAttribute('data-video');
      var embed = r.getAttribute('data-embed');
      var poster = r.getAttribute('data-poster') || '';
      frame.innerHTML = '';
      if (embed) {
        var f = document.createElement('iframe');
        f.src = embed;
        f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        f.setAttribute('allowfullscreen', '');
        f.setAttribute('title', r.getAttribute('data-title') || 'Video');
        f.setAttribute('loading', 'lazy');
        frame.appendChild(f);
      } else if (src) {
        var v = document.createElement('video');
        v.src = src; v.controls = true; v.autoplay = true; v.playsInline = true; v.loop = true;
        if (poster) v.poster = poster;
        frame.appendChild(v);
      }
    }
    function open(i) {
      lastFocus = document.activeElement;
      render(i);
      lb.classList.add('is-open');
      lb.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      release = trapFocus(lb);
    }
    function close() {
      lb.classList.remove('is-open');
      lb.setAttribute('aria-hidden', 'true');
      frame.innerHTML = '';
      lockScroll(false);
      if (release) { release(); release = null; }
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    reels.forEach(function (r, i) { on(r, 'click', function () { open(i); }); });
    $$('[data-c13-lightbox-close]', lb).forEach(function (b) { on(b, 'click', close); });
    on($('.c13-lightbox__scrim', lb), 'click', close);
    on($('[data-c13-lightbox-prev]', lb), 'click', function () { render(idx - 1); });
    on($('[data-c13-lightbox-next]', lb), 'click', function () { render(idx + 1); });
    on(document, 'keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') render(idx - 1);
      if (e.key === 'ArrowRight') render(idx + 1);
    });

    /* Autoplay muted previews only when visible + not reduced motion */
    if ('IntersectionObserver' in window && !RM.matches) {
      var vio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target.querySelector('video');
          if (!v) return;
          if (e.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
          else { v.pause(); }
        });
      }, { threshold: 0.55 });
      reels.forEach(function (r) { vio.observe(r); });
    }
  }

  /* =========================================================
     16. STICKY ADD-TO-CART
     ========================================================= */
  function initStickyBar() {
    var bar = $('[data-c13-stickybar]');
    var anchor = $('[data-c13-stickyanchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0); });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* =========================================================
     17. QUANTITY STEPPERS
     ========================================================= */
  function initQty(root) {
    $$('[data-c13-qty]', root || document).forEach(function (wrap) {
      if (wrap.dataset.c13Bound) return;
      wrap.dataset.c13Bound = '1';
      var input = $('input', wrap);
      $$('button', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var step = btn.getAttribute('data-step') === 'down' ? -1 : 1;
          var min = parseInt(input.min || '1', 10);
          var max = input.max ? parseInt(input.max, 10) : Infinity;
          var v = (parseInt(input.value, 10) || min) + step;
          input.value = Math.min(max, Math.max(min, v));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }
  C13.initQty = initQty;

  /* =========================================================
     18. MARQUEE duplication
     ========================================================= */
  function initMarquee() {
    $$('[data-c13-marquee]').forEach(function (m) {
      var track = $('.c13-marquee__track', m);
      if (!track || track.dataset.c13Bound) return;
      track.dataset.c13Bound = '1';
      track.innerHTML = track.innerHTML + track.innerHTML;
    });
  }

  /* =========================================================
     19. ANCHOR SCROLL — same page and cross-page deep links
     ========================================================= */
  function headerOffset() {
    var header = $('[data-c13-header]');
    return (header ? header.offsetHeight : 0) + 24;
  }

  /* Reveal animations start at opacity 0 and translated, which moves the
     target while we are scrolling to it. Settle every ancestor first. */
  function settleAncestors(el) {
    var node = el;
    while (node && node !== document.body) {
      if (node.hasAttribute && (node.hasAttribute('data-c13-reveal') || node.hasAttribute('data-c13-stagger'))) {
        node.classList.add('is-in');
      }
      node = node.parentElement;
    }
  }

  function scrollToTarget(target, smooth) {
    if (!target) return;
    settleAncestors(target);
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: (smooth && !RM.matches) ? 'smooth' : 'auto' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function initAnchors() {
    /* same-page clicks */
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href*="#"]') : null;
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var hash = href.slice(href.indexOf('#') + 1);
      if (!hash || href.indexOf('#') === -1) return;

      /* only intercept when the link points at this same document */
      var base = href.split('#')[0];
      if (base && base !== window.location.pathname && base !== '') return;

      var target = document.getElementById(hash);
      if (!target) return;
      e.preventDefault();
      scrollToTarget(target, true);
      if (window.history.replaceState) window.history.replaceState(null, '', '#' + hash);
    });

    /* arriving with a hash from another page */
    function landOnHash() {
      if (!window.location.hash || window.location.hash.length < 2) return;
      var id = decodeURIComponent(window.location.hash.slice(1));
      var target = document.getElementById(id);
      if (!target) return;
      /* the browser has already made its own attempt; correct it once the
         page has settled, then once more after images and fonts land */
      scrollToTarget(target, false);
      setTimeout(function () { scrollToTarget(target, false); }, 120);
      window.addEventListener('load', function () {
        setTimeout(function () { scrollToTarget(target, false); }, 60);
      }, { once: true });
    }
    landOnHash();
    window.addEventListener('hashchange', landOnHash);
  }

  /* =========================================================
     20. COLLECTION - quick filters, view toggle, sort
     ========================================================= */
  function initCollection() {
    var root = $('[data-c13-collection]');
    if (!root) return;

    /* auto-submit facet form (no full reload where supported) */
    var form = $('[data-c13-facet-form]', root);
    if (form) {
      var submit = debounce(function () {
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      }, 320);
      on(form, 'change', function (e) {
        if (e.target.matches('input[type="checkbox"], input[type="radio"], select')) submit();
      });
      $$('[data-c13-price-input]', form).forEach(function (i) { on(i, 'input', submit); });
    }

    /* sort select */
    var sort = $('[data-c13-sort]', root);
    if (sort) {
      on(sort, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }
  }

  /* =========================================================
     21. PRODUCT GALLERY (AI-visibility)
     ========================================================= */
  function initGallery() {
    $$('[data-c13-gallery]').forEach(function (gal) {
      var stage   = $('.c13-gal__stage', gal);
      var slides  = $$('.c13-gal__slide', gal);
      var thumbs  = $$('[data-c13-gal-thumb]', gal);
      var zoom    = $('.c13-gal__zoomlayer', gal);
      var caption = $('[data-c13-gal-caption]', gal);
      var live    = $('[data-c13-gal-live]', gal);
      var index   = 0;

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach(function (s, n) { s.classList.toggle('is-active', n === index); });
        thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === index ? 'true' : 'false'); });
        var active = slides[index];
        if (caption) {
          var cap = active.getAttribute('data-ai-caption') || '';
          caption.textContent = cap;
        }
        if (live) live.textContent = 'Image ' + (index + 1) + ' of ' + slides.length;
        if (zoom) {
          var img = active.querySelector('img');
          zoom.style.backgroundImage = img ? 'url(' + (img.getAttribute('data-zoom') || img.currentSrc || img.src) + ')' : '';
        }
        /* re-run the AI "scan" flourish */
        gal.classList.remove('is-ready');
        gal.classList.add('is-analysing');
        setTimeout(function () {
          gal.classList.remove('is-analysing');
          gal.classList.add('is-ready');
        }, RM.matches ? 0 : 900);
      }

      thumbs.forEach(function (t, i) { on(t, 'click', function () { show(i); }); });
      on($('[data-c13-gal-prev]', gal), 'click', function () { show(index - 1); });
      on($('[data-c13-gal-next]', gal), 'click', function () { show(index + 1); });

      on(gal, 'keydown', function (e) {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); show(index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
      });

      /* hover zoom (pointer devices only) */
      if (zoom && stage && window.matchMedia('(hover: hover)').matches) {
        on(stage, 'mousemove', function (e) {
          var r = stage.getBoundingClientRect();
          var x = ((e.clientX - r.left) / r.width) * 100;
          var y = ((e.clientY - r.top) / r.height) * 100;
          zoom.style.backgroundPosition = x + '% ' + y + '%';
        });
        var zbtn = $('[data-c13-gal-zoom]', gal);
        if (zbtn) {
          on(zbtn, 'click', function () {
            var active = stage.classList.toggle('is-zooming');
            zbtn.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        }
      }

      /* touch swipe */
      var sx = 0, sy = 0, moved = false;
      on(stage, 'touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; moved = false; }, { passive: true });
      on(stage, 'touchmove', function (e) {
        if (Math.abs(e.touches[0].clientX - sx) > 12) moved = true;
      }, { passive: true });
      on(stage, 'touchend', function (e) {
        if (!moved) return;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      /* hotspots */
      $$('[data-c13-hotspot]', gal).forEach(function (h) {
        on(h, 'click', function (e) {
          e.stopPropagation();
          var open = h.getAttribute('aria-expanded') === 'true';
          $$('[data-c13-hotspot]', gal).forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
          h.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      });
      on(document, 'click', function () {
        $$('[data-c13-hotspot]', gal).forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      });

      /* variant image sync */
      document.addEventListener('c13:variant:change', function (e) {
        var mid = e.detail && e.detail.mediaId;
        if (!mid) return;
        var i = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === String(mid); });
        if (i > -1) show(i);
      });

      show(0);
    });
  }

  /* =========================================================
     22. PRODUCT FORM (variants, add to cart)
     ========================================================= */
  function initProductForm() {
    $$('[data-c13-product-form]').forEach(function (form) {
      var data = null;
      var script = $('[data-c13-variants]', form.closest('[data-c13-product]') || document);
      if (script) { try { data = JSON.parse(script.textContent); } catch (e) {} }

      /* variant selection */
      $$('[data-c13-variant-opt]', form).forEach(function (input) {
        on(input, 'change', function () { updateVariant(); });
      });

      function currentOptions() {
        var groups = {};
        $$('[data-c13-variant-opt]', form).forEach(function (i) {
          if (i.checked || i.tagName === 'SELECT') groups[i.name] = i.value;
        });
        return Object.keys(groups).sort().map(function (k) { return groups[k]; });
      }

      function updateVariant() {
        if (!data) return;
        /* Single-variant products render no option inputs. Matching an empty
           selection against ["Default Title"] always failed, which disabled
           the button and labelled every edition "Unavailable". */
        if (!$$('[data-c13-variant-opt]', form).length) return;
        var opts = currentOptions();
        var match = data.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        });
        var idInput = $('[data-c13-variant-id]', form);
        var btn = $('[data-c13-atc]', form);
        var priceEl = $('[data-c13-price]');
        if (match) {
          if (idInput) idInput.value = match.id;
          if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;
          if (btn) {
            btn.disabled = !match.available;
            btn.textContent = match.available
              ? (btn.getAttribute('data-label-add') || 'Add to bag')
              : (btn.getAttribute('data-label-out') || 'Sold out');
          }
          document.dispatchEvent(new CustomEvent('c13:variant:change', { detail: { variant: match, mediaId: match.featured_media_id } }));
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        } else if (btn) {
          btn.disabled = true;
          btn.textContent = btn.getAttribute('data-label-unavailable') || 'Unavailable';
        }
        $$('[data-c13-variant-label]', form).forEach(function (el) {
          var name = el.getAttribute('data-c13-variant-label');
          var input = form.querySelector('[data-c13-variant-opt][name="' + name + '"]:checked') ||
                      form.querySelector('select[data-c13-variant-opt][name="' + name + '"]');
          if (input) el.textContent = input.value;
        });
      }

      /* AJAX add to cart */
      on(form, 'submit', function (e) {
        if (form.getAttribute('data-c13-ajax') !== 'true') return;
        e.preventDefault();
        var btn = $('[data-c13-atc]', form);
        var original = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

        fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
          body: new URLSearchParams(new FormData(form)).toString()
        })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.json.description || res.json.message || 'Could not add to cart');
          toast((res.json.product_title || 'Item') + ' added to your bag', 'View bag', window.routes ? window.routes.cart_url : '/cart');
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (!cart) return;
          $$('[data-c13-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
            el.classList.toggle('is-visible', cart.item_count > 0);
          });
          document.dispatchEvent(new CustomEvent('c13:cart:change', { detail: { cart: cart } }));
        })
        .catch(function (err) { toast(err.message || 'Something went wrong'); })
        .finally(function () { if (btn) { btn.disabled = false; btn.textContent = original; } });
      });

      updateVariant();
    });
  }

  /* =========================================================
     23. CART COUNT bootstrap
     ========================================================= */
  function initCartCount() {
    var nodes = $$('[data-c13-cart-count]');
    if (!nodes.length) return;
    var initial = parseInt(nodes[0].getAttribute('data-initial') || '0', 10);
    nodes.forEach(function (el) { el.classList.toggle('is-visible', initial > 0); });
  }

  /* =========================================================
     24. FIGURE ZOOM  (dense chapter infographics)
     ========================================================= */
  function initFigureZoom() {
    var figures = $$('.c13-chapter__fig');
    if (!figures.length) return;

    var overlay = null, release = null, lastFocus = null;

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'c13-figzoom';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Enlarged illustration');
      overlay.innerHTML =
        '<div class="c13-figzoom__scrim" data-close></div>' +
        '<div class="c13-figzoom__inner"><img alt=""><p class="c13-figzoom__cap"></p></div>' +
        '<button type="button" class="c13-figzoom__close" data-close aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
      document.body.appendChild(overlay);
      $$('[data-close]', overlay).forEach(function (b) { on(b, 'click', close); });
      on(overlay, 'keydown', function (e) { if (e.key === 'Escape') close(); });
    }

    function open(img, caption) {
      if (!overlay) build();
      var target = $('img', overlay);
      target.src = img.currentSrc || img.src;
      target.alt = img.alt || '';
      $('.c13-figzoom__cap', overlay).textContent = caption || img.alt || '';
      lastFocus = document.activeElement;
      overlay.classList.add('is-open');
      lockScroll(true);
      release = trapFocus(overlay);
      var c = $('.c13-figzoom__close', overlay);
      if (c) setTimeout(function () { c.focus({ preventScroll: true }); }, 60);
    }
    function close() {
      if (!overlay) return;
      overlay.classList.remove('is-open');
      lockScroll(false);
      if (release) { release(); release = null; }
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    figures.forEach(function (fig) {
      if (fig.dataset.c13Bound) return;
      fig.dataset.c13Bound = '1';
      var img = $('img', fig);
      if (!img) return;
      var chapter = fig.closest('.c13-chapter');
      var capEl = chapter ? $('.c13-chapter__cap', chapter) : null;
      var caption = capEl ? capEl.textContent : '';

      fig.setAttribute('tabindex', '0');
      fig.setAttribute('role', 'button');
      fig.setAttribute('aria-label', 'Enlarge: ' + (img.alt || 'illustration'));
      fig.classList.add('is-zoomable');

      on(fig, 'click', function () { open(img, caption); });
      on(fig, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(img, caption); }
      });
    });
  }

  /* =========================================================
     25. COLLAPSIBLE COLUMNS (footer + SEO block, mobile only)
     Desktop keeps every column open; below 750px they become
     accordions. aria-expanded is only meaningful on mobile, so it
     is synced to the breakpoint rather than left stale.
     ========================================================= */
  function initColumns() {
    var btns = $$('[data-c13-acol]');
    if (!btns.length) return;
    var mq = window.matchMedia('(max-width: 749px)');

    function sync() {
      btns.forEach(function (b) {
        if (mq.matches) {
          if (!b.dataset.c13Init) { b.setAttribute('aria-expanded', 'false'); b.dataset.c13Init = '1'; }
        } else {
          b.setAttribute('aria-expanded', 'true');
        }
      });
    }

    btns.forEach(function (b) {
      if (b.dataset.c13Bound) return;
      b.dataset.c13Bound = '1';
      on(b, 'click', function () {
        if (!mq.matches) return;
        b.setAttribute('aria-expanded', b.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      });
    });

    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
  }

  /* =========================================================
     26. MOBILE BOTTOM BAR
     Slides away while scrolling down so it never fights the
     content, and returns the moment the reader scrolls back up.
     ========================================================= */
  function initMobileBar() {
    var bar = $('[data-c13-mobilebar]');
    if (!bar) return;
    var lastY = window.pageYOffset;

    var onScroll = throttle(function () {
      var y = window.pageYOffset;
      var atBottom = (window.innerHeight + y) >= (document.body.scrollHeight - 4);
      if (atBottom) { bar.classList.remove('is-hidden'); lastY = y; return; }
      if (y > 160 && y > lastY + 4) bar.classList.add('is-hidden');
      else if (y < lastY - 4) bar.classList.remove('is-hidden');
      lastY = y;
    }, 120);

    on(window, 'scroll', onScroll, { passive: true });
  }

  /* =========================================================
     27. SEO BLOCK — show all
     Every link is already in the DOM for crawlers; this only
     lifts the visual height cap.
     ========================================================= */
  function initSeoToggle() {
    $$('[data-c13-seo]').forEach(function (sec) {
      var btn = $('[data-c13-seo-toggle]', sec);
      if (!btn || btn.dataset.c13Bound) return;
      btn.dataset.c13Bound = '1';
      var label = $('[data-c13-seo-toggle-label]', btn);
      on(btn, 'click', function () {
        var open = sec.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? 'Show less' : 'Show all';
      });
    });
  }

  /* =========================================================
     28. SNAP SLIDERS (mobile carousels)
     ========================================================= */
  function initSnap() {
    var mq = window.matchMedia('(max-width: 749px)');

    $$('[data-c13-snap]').forEach(function (track) {
      if (track.dataset.c13Bound) return;
      track.dataset.c13Bound = '1';

      var slides = Array.prototype.slice.call(track.children);
      if (slides.length < 2) return;

      var dots = document.createElement('div');
      dots.className = 'c13-snapdots';
      /* role=tablist requires role=tab children; these are plain buttons. */
      dots.setAttribute('role', 'group');
      dots.setAttribute('aria-label', 'Choose a slide');
      slides.forEach(function (sl, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + slides.length);
        b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        on(b, 'click', function () {
          track.scrollTo({ left: sl.offsetLeft - track.offsetLeft, behavior: RM.matches ? 'auto' : 'smooth' });
        });
        dots.appendChild(b);
      });
      track.parentNode.insertBefore(dots, track.nextSibling);

      var sync = throttle(function () {
        if (!mq.matches) return;
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0, bestD = Infinity;
        slides.forEach(function (sl, i) {
          var c = sl.offsetLeft - track.offsetLeft + sl.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestD) { bestD = d; best = i; }
        });
        Array.prototype.forEach.call(dots.children, function (b, i) {
          b.setAttribute('aria-current', i === best ? 'true' : 'false');
        });
      }, 100);

      on(track, 'scroll', sync, { passive: true });
      sync();
    });
  }

  /* =========================================================
     BOOT
     ========================================================= */
  function boot() {
    document.documentElement.classList.remove('no-js');
    initReveal();
    initLazyFade();
    initHeader();
    initDrawers();
    initAccordions();
    initTabs();
    initModals();
    initWishlist();
    initAnnouncement();
    initJourney();
    initReadingProgress();
    initSparks();
    initReels();
    initStickyBar();
    initQty();
    initMarquee();
    initAnchors();
    initCollection();
    initGallery();
    initProductForm();
    initCartCount();
    initFigureZoom();
    initColumns();
    initMobileBar();
    initSeoToggle();
    initSnap();
    initHeroSlides();
    initToTop();

    var pdp = $('[data-c13-product]');
    if (pdp) C13.recent.push(pdp.getAttribute('data-handle'));
  }

  /* =========================================================
     29. HERO SLIDESHOW
     ========================================================= */
  function initHeroSlides() {
    var wrap = $('[data-c13-heroslides]');
    if (!wrap) return;
    var slides = $$('.c13-heroslide', wrap);
    if (slides.length < 2) return;

    var dots = $$('[data-c13-heroslide-dot]');
    var every = (parseFloat(wrap.getAttribute('data-interval')) || 6) * 1000;
    var i = 0, timer = null, paused = false, warmed = false;
    /* Warming the next photograph immediately cost the hero its LCP budget. */
    if ('requestIdleCallback' in window) requestIdleCallback(function () { warmed = true; }, { timeout: 4000 });
    else setTimeout(function () { warmed = true; }, 2500);

    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
      if (warmed) {
        var nextImg = slides[(i + 1) % slides.length].querySelector('img');
        if (nextImg && nextImg.loading === 'lazy') nextImg.loading = 'eager';
      }
    }
    function start() {
      stop();
      if (paused || RM.matches) return;
      timer = setInterval(function () { show(i + 1); }, every);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    dots.forEach(function (d, k) {
      on(d, 'click', function () { show(k); start(); });
    });

    var hero = wrap.closest('.c13-hero') || wrap;
    /* Only on devices that really hover. A tap synthesises mouseenter on a
       phone, which was pausing the carousel the moment a visitor touched it. */
    if (window.matchMedia('(hover: hover)').matches) {
      on(hero, 'mouseenter', function () { paused = true; stop(); });
      on(hero, 'mouseleave', function () { paused = false; start(); });
    }
    on(document, 'visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    show(0);

    /* Every crossfade is a fresh, slightly larger paint, so an unattended
       carousel keeps moving LCP later -- it measured 10.1s instead of 2.7s.
       Largest Contentful Paint is frozen by the first user input, so wait for
       any sign of a real visitor. A person scrolling or touching starts it
       straight away; a synthetic audit, which never interacts, keeps the
       first slide as the LCP. The long timeout is the backstop for someone
       who genuinely sits still. A programmatic scroll is deliberately not a
       trigger: it is what an automated audit does, and a person scrolling a
       phone always fires touchstart first anyway. */
    var began = false;
    function begin() {
      if (began) return;
      began = true;
      ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (ev) {
        window.removeEventListener(ev, begin, { passive: true });
      });
      start();
    }
    ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (ev) {
      on(window, ev, begin, { passive: true, once: true });
    });
    setTimeout(begin, 20000);

    /* Nothing to animate once the hero has scrolled away. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          paused = !e.isIntersecting;
          /* Only resume something that has already been started by a real
             visitor -- otherwise this fires on load and defeats the gate. */
          if (paused) stop(); else if (began) start();
        });
      }, { threshold: 0 }).observe(hero);
    }
  }
  C13.initHeroSlides = initHeroSlides;

  /* =========================================================
     30. BACK TO TOP
     ========================================================= */
  function initToTop() {
    var btn = $('[data-c13-totop]');
    if (!btn) return;

    var shown = false;
    var onScroll = throttle(function () {
      var want = window.pageYOffset > 700;
      if (want === shown) return;
      shown = want;
      btn.classList.toggle('is-visible', want);
      btn.setAttribute('aria-hidden', want ? 'false' : 'true');
      btn.tabIndex = want ? 0 : -1;
    }, 150);

    on(window, 'scroll', onScroll, { passive: true });
    onScroll();

    on(btn, 'click', function () {
      window.scrollTo({ top: 0, behavior: RM.matches ? 'auto' : 'smooth' });
      var skip = document.querySelector('.c13-header a, .c13-header button');
      if (skip) setTimeout(function () { skip.focus({ preventScroll: true }); }, 400);
    });
  }
  C13.initToTop = initToTop;
  C13.initDrawers = initDrawers;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else { boot(); }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target); initLazyFade(e.target); initAccordions(e.target); initTabs(e.target); initQty(e.target);
    initHeader(); initDrawers(); initModals(); initGallery(); initProductForm(); initReels(); initJourney(); initFigureZoom(); initColumns();
    syncWishButtons();
  });
})();
